import { useAuth } from "@/lib/auth";
import { getIcConfig } from "@/lib/network";
import { getV2ProtocolActor, hasProtocolEnv } from "@/lib/protocol-actor";
import {
	buyMmkWithIcp,
	buyMmkWithUsdm,
	createTextProposal,
	depositSavings,
	getGasFundingStatusUi,
	getMmkSwapParams,
	getSavingsBalance,
	getSavingsRateApr,
	getStakingBalance,
	getTokenomicsStatusUi,
	quoteBuyMmkWithIcp,
	quoteMmkSwap,
	stakeMmk,
	unstakeMmk,
	voteProposal,
	withdrawSavings,
	type GasFundingStatusUi,
	type MmkSwapParamsUi,
	type TokenomicsUi,
} from "@motokomaker/ic-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export function useMmkSwapParams() {
	return useQuery({
		queryKey: ["v2", "mmkSwapParams"],
		queryFn: async (): Promise<MmkSwapParamsUi | null> => {
			const actor = await getV2ProtocolActor();
			if (!actor || !hasProtocolEnv()) return null;
			return getMmkSwapParams(actor);
		},
		staleTime: 20_000,
	});
}

export function useTokenomicsStatus() {
	return useQuery({
		queryKey: ["v2", "tokenomics"],
		queryFn: async (): Promise<TokenomicsUi | null> => {
			const actor = await getV2ProtocolActor();
			if (!actor || !hasProtocolEnv()) return null;
			return getTokenomicsStatusUi(actor);
		},
		staleTime: 30_000,
	});
}

export function useQuoteMmk(usdmIn: number) {
	return useQuery({
		queryKey: ["v2", "quoteMmk", usdmIn],
		queryFn: async () => {
			const actor = await getV2ProtocolActor();
			if (!actor || usdmIn <= 0) return null;
			return quoteMmkSwap(actor, usdmIn);
		},
		enabled: hasProtocolEnv() && usdmIn > 0,
		staleTime: 10_000,
	});
}

export function useBuyMmkWithUsdm() {
	const { actor, agent, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (usdmIn: number) => {
			if (!actor || !agent) throw new Error("Connect Internet Identity");
			return buyMmkWithUsdm(actor, agent, getIcConfig(), usdmIn);
		},
		onSuccess: (mmkOut) => {
			toast.success(
				`Purchased ${mmkOut.toFixed(4)} MMK — stake to gain vote weight`,
			);
			void qc.invalidateQueries({ queryKey: ["v2", "mmkSwapParams"] });
			void qc.invalidateQueries({ queryKey: ["v2", "tokenomics"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useGasFundingStatus() {
	return useQuery({
		queryKey: ["v2", "gasFunding"],
		queryFn: async (): Promise<GasFundingStatusUi | null> => {
			const actor = await getV2ProtocolActor();
			if (!actor || !hasProtocolEnv()) return null;
			return getGasFundingStatusUi(actor);
		},
		staleTime: 20_000,
	});
}

export function useQuoteMmkWithIcp(icpIn: number) {
	return useQuery({
		queryKey: ["v2", "quoteMmkIcp", icpIn],
		queryFn: async () => {
			const actor = await getV2ProtocolActor();
			if (!actor || icpIn <= 0) return null;
			return quoteBuyMmkWithIcp(actor, icpIn);
		},
		enabled: hasProtocolEnv() && icpIn > 0,
		staleTime: 10_000,
	});
}

export function useBuyMmkWithIcp() {
	const { actor, agent, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (icpIn: number) => {
			if (!actor || !agent) throw new Error("Connect Internet Identity");
			return buyMmkWithIcp(actor, agent, getIcConfig(), icpIn);
		},
		onSuccess: (mmkOut) => {
			toast.success(
				`Purchased ${mmkOut.toFixed(4)} MMK with ICP (Gas Funding) — stake to gain vote weight`,
			);
			void qc.invalidateQueries({ queryKey: ["v2", "mmkSwapParams"] });
			void qc.invalidateQueries({ queryKey: ["v2", "tokenomics"] });
			void qc.invalidateQueries({ queryKey: ["v2", "gasFunding"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useStakingBalance() {
	const { actor, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "stakingBalance"],
		queryFn: async () => {
			if (!actor) return 0;
			return getStakingBalance(actor);
		},
		enabled: isAuthenticated && !!actor && hasProtocolEnv(),
		staleTime: 15_000,
	});
}

export function useStakeMmk() {
	const { actor, agent, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (amount: number) => {
			if (!actor || !agent) throw new Error("Connect Internet Identity");
			await stakeMmk(actor, agent, getIcConfig(), amount);
		},
		onSuccess: (_, amount) => {
			toast.success(`Staked ${amount} MMK`);
			void qc.invalidateQueries({ queryKey: ["v2", "stakingBalance"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useUnstakeMmk() {
	const { actor, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (amount: number) => {
			if (!actor) throw new Error("Connect Internet Identity");
			await unstakeMmk(actor, amount);
		},
		onSuccess: (_, amount) => {
			toast.success(`Unstaked ${amount} MMK`);
			void qc.invalidateQueries({ queryKey: ["v2", "stakingBalance"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useVoteProposal() {
	const { actor } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			id,
			support,
		}: {
			id: bigint | number;
			support: boolean;
		}) => {
			if (!actor) throw new Error("Connect Internet Identity");
			await voteProposal(actor, BigInt(id), support);
		},
		onSuccess: (_, { support }) => {
			toast.success(support ? "Voted for" : "Voted against");
			void qc.invalidateQueries({ queryKey: ["v2", "public", "governance"] });
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useCreateProposal() {
	const { actor } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			title,
			description,
		}: {
			title: string;
			description: string;
		}) => {
			if (!actor) throw new Error("Connect Internet Identity");
			return createTextProposal(actor, title, description);
		},
		onSuccess: (id) => {
			toast.success(`Proposal #${id.toString()} created`);
			void qc.invalidateQueries({ queryKey: ["v2", "public", "governance"] });
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useExecuteProposal() {
	const { actor } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (id: bigint | number) => {
			if (!actor) throw new Error("Connect Internet Identity");
			const { unwrapResult } = await import("@motokomaker/ic-client");
			unwrapResult(
				await actor.executeProposal(BigInt(id)),
				"executeProposal",
			);
		},
		onSuccess: (_, id) => {
			toast.success(`Executed proposal #${id.toString()}`);
			void qc.invalidateQueries({ queryKey: ["v2", "public", "governance"] });
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useSavingsBalance() {
	const { actor, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "savingsBalance"],
		queryFn: async () => {
			if (!actor) return 0;
			return getSavingsBalance(actor);
		},
		enabled: isAuthenticated && !!actor && hasProtocolEnv(),
		staleTime: 15_000,
	});
}

export function useSavingsRateApr() {
	return useQuery({
		queryKey: ["v2", "savingsApr"],
		queryFn: async () => {
			const actor = await getV2ProtocolActor();
			if (!actor) return null;
			return getSavingsRateApr(actor);
		},
		staleTime: 60_000,
	});
}

export function useDepositSavings() {
	const { actor, agent, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (amount: number) => {
			if (!actor || !agent) throw new Error("Connect Internet Identity");
			await depositSavings(actor, agent, getIcConfig(), amount);
		},
		onSuccess: (_, amount) => {
			toast.success(`Deposited ${amount} USDM to savings`);
			void qc.invalidateQueries({ queryKey: ["v2", "savingsBalance"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useWithdrawSavings() {
	const { actor, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (amount: number) => {
			if (!actor) throw new Error("Connect Internet Identity");
			await withdrawSavings(actor, amount);
		},
		onSuccess: (_, amount) => {
			toast.success(`Withdrew ${amount} USDM from savings`);
			void qc.invalidateQueries({ queryKey: ["v2", "savingsBalance"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}
